<template>
  <ts-modal
    ref="modal"
    modal-title="Move candidates to Preselected list"
  >
    <template v-slot:modal-content>
      <div class="alert alert-info text-center">
        {{ infoMessage }}
      </div>
      <div
        v-if="errorMessageVisible"
        class="alert alert-danger"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="candidateNameVisible"
        class="row gx-2"
      >
        <div class="mb-3 col-12">
          <label class="form-label" for="candidate">Candidate</label>
          <input
            v-model="candidateName"
            type="text"
            class="form-control form-control-sm"
            readonly
          >
        </div>
      </div>
      <div class="row gx-2">
        <div class="mb-3 col-md-6">
          <label
            for="priority"
            class="form-label required"
          >
            Priority
          </label>
          <input
            v-model="priority"
            type="text"
            class="form-control form-control-sm"
          >
          <small class="form-text text-muted">Enter numerical value, "RESERVE" or "RESERVE X" where X is a number</small>
        </div>
      </div>
    </template>
    <template v-slot:modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        @click.prevent="onApprove"
      >
        Select
      </button>
    </template>
  </ts-modal>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import axios from 'axios'
import Rails from '@rails/ujs'

import TsModal from '../ts-modal.vue'
import { getAxiosError } from '@/js/axios-utils'
import { isNonEmptyString } from '@/js/js-utils'
import type { GridID } from '../types'

const emit = defineEmits<{
  "success": []
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')

const priority = ref<string>('')
const url = ref<string>('')
const candidateIds = ref<GridID[]>([])
const errorMessage = ref<string>('')
const isLoading = ref<boolean>(false)
const candidateName = ref<string | null>(null)

const errorMessageVisible = computed((): boolean => {
  return isNonEmptyString(errorMessage.value)
})

const multipleCandidates = computed((): boolean => {
  return candidateIds.value.length > 1
})

const infoMessage = computed((): string => {
  if (multipleCandidates.value) {
    return 'You are about to move selected candidates to Preselected candidates list'
  } else {
    return 'You are about to move candidate to Preselected candidates list'
  }
})

const candidateNameVisible = computed((): boolean => {
  return isNonEmptyString(candidateName.value)
})

const show = (newCandidateIds: GridID[], newUrl: string, newCandidateName?: string): void => {
  candidateIds.value = newCandidateIds
  url.value = newUrl
  candidateName.value = isNonEmptyString(newCandidateName) ? newCandidateName : null
  modal.value?.show()
}

const hide = (): void => {
  modal.value?.hide()
}

const onApprove = (): void => {
  errorMessage.value = ''
  const postData = {
    candidate_ids: candidateIds.value,
    priority: priority.value,
  }

  isLoading.value = true
  axios.post(url.value, postData, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken()
    },
  })
    .then((response) => {
      if (response.status == 204) {
        emit('success')
      } else if (response.data && isNonEmptyString(response.data.error)) {
        errorMessage.value = response.data.error
      } else {
        errorMessage.value = 'Action failed'
      }
    })
    .catch((error) => {
      errorMessage.value = getAxiosError(error) || ''
    })
    .then(() => {
      isLoading.value = false
    })
}

defineExpose({
  hide,
  show,
})
</script>
